import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

export class Tokens {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * List all the tokens created for a specific file or bucket. You can use the query params to filter your results.
     *
     * @param {string} params.bucketId - Storage bucket unique ID. You can create a new storage bucket using the Storage service [server integration](https://appwrite.io/docs/server/storage#createBucket).
     * @param {string} params.fileId - File unique ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: expire
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceTokenList>}
     */
    list(params: {
        bucketId: string;
        fileId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ResourceTokenList>;
    /**
     * List all the tokens created for a specific file or bucket. You can use the query params to filter your results.
     *
     * @param {string} bucketId - Storage bucket unique ID. You can create a new storage bucket using the Storage service [server integration](https://appwrite.io/docs/server/storage#createBucket).
     * @param {string} fileId - File unique ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: expire
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceTokenList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    list(
        bucketId: string,
        fileId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ResourceTokenList>;
    list(
        paramsOrFirst:
            | {
                  bucketId: string;
                  fileId: string;
                  queries?: string[];
                  total?: boolean;
              }
            | string,
        ...rest: [string?, string[]?, boolean?]
    ): Promise<Models.ResourceTokenList> {
        let params: {
            bucketId: string;
            fileId: string;
            queries?: string[];
            total?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                bucketId: string;
                fileId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                bucketId: paramsOrFirst as string,
                fileId: rest[0] as string,
                queries: rest[1] as string[],
                total: rest[2] as boolean,
            };
        }

        const bucketId = params.bucketId;
        const fileId = params.fileId;
        const queries = params.queries;
        const total = params.total;

        if (typeof bucketId === 'undefined' || bucketId === '') {
            throw new AppwriteException(
                'Missing required parameter: "bucketId"',
            );
        }
        if (typeof fileId === 'undefined' || fileId === '') {
            throw new AppwriteException('Missing required parameter: "fileId"');
        }
        const apiPath = '/tokens/buckets/{bucketId}/files/{fileId}'
            .replace('{bucketId}', encodeURIComponent(String(bucketId)))
            .replace('{fileId}', encodeURIComponent(String(fileId)));
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'resourceTokenList',
            models: {
                resourceTokenList: {
                    defaults: {
                        total: 0,
                        tokens: [],
                    },
                    properties: {
                        tokens: {
                            items: 'resourceToken',
                        },
                    },
                },
                resourceToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        resourceId: '',
                        resourceType: '',
                        expire: '',
                        secret: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new token. A token is linked to a file. Token can be passed as a request URL search parameter.
     *
     * @param {string} params.bucketId - Storage bucket unique ID. You can create a new storage bucket using the Storage service [server integration](https://appwrite.io/docs/server/storage#createBucket).
     * @param {string} params.fileId - File unique ID.
     * @param {string | null} params.expire - Token expiry date
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     */
    createFileToken(params: {
        bucketId: string;
        fileId: string;
        expire?: string | null;
    }): Promise<Models.ResourceToken>;
    /**
     * Create a new token. A token is linked to a file. Token can be passed as a request URL search parameter.
     *
     * @param {string} bucketId - Storage bucket unique ID. You can create a new storage bucket using the Storage service [server integration](https://appwrite.io/docs/server/storage#createBucket).
     * @param {string} fileId - File unique ID.
     * @param {string | null} expire - Token expiry date
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createFileToken(
        bucketId: string,
        fileId: string,
        expire?: string | null,
    ): Promise<Models.ResourceToken>;
    createFileToken(
        paramsOrFirst:
            | { bucketId: string; fileId: string; expire?: string | null }
            | string,
        ...rest: [string?, (string | null)?]
    ): Promise<Models.ResourceToken> {
        let params: {
            bucketId: string;
            fileId: string;
            expire?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                bucketId: string;
                fileId: string;
                expire?: string | null;
            };
        } else {
            params = {
                bucketId: paramsOrFirst as string,
                fileId: rest[0] as string,
                expire: rest[1] as string | null,
            };
        }

        const bucketId = params.bucketId;
        const fileId = params.fileId;
        const expire = params.expire;

        if (typeof bucketId === 'undefined' || bucketId === '') {
            throw new AppwriteException(
                'Missing required parameter: "bucketId"',
            );
        }
        if (typeof fileId === 'undefined' || fileId === '') {
            throw new AppwriteException('Missing required parameter: "fileId"');
        }
        const apiPath = '/tokens/buckets/{bucketId}/files/{fileId}'
            .replace('{bucketId}', encodeURIComponent(String(bucketId)))
            .replace('{fileId}', encodeURIComponent(String(fileId)));
        const apiPayload: Payload = {};
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'resourceToken',
            models: {
                resourceToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        resourceId: '',
                        resourceType: '',
                        expire: '',
                        secret: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a token by its unique ID.
     *
     * @param {string} params.tokenId - Token ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     */
    get(params: { tokenId: string }): Promise<Models.ResourceToken>;
    /**
     * Get a token by its unique ID.
     *
     * @param {string} tokenId - Token ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    get(tokenId: string): Promise<Models.ResourceToken>;
    get(
        paramsOrFirst: { tokenId: string } | string,
    ): Promise<Models.ResourceToken> {
        let params: { tokenId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { tokenId: string };
        } else {
            params = {
                tokenId: paramsOrFirst as string,
            };
        }

        const tokenId = params.tokenId;

        if (typeof tokenId === 'undefined' || tokenId === '') {
            throw new AppwriteException(
                'Missing required parameter: "tokenId"',
            );
        }
        const apiPath = '/tokens/{tokenId}'.replace(
            '{tokenId}',
            encodeURIComponent(String(tokenId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'resourceToken',
            models: {
                resourceToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        resourceId: '',
                        resourceType: '',
                        expire: '',
                        secret: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a token by its unique ID. Use this endpoint to update a token's expiry date.
     *
     * @param {string} params.tokenId - Token unique ID.
     * @param {string | null} params.expire - File token expiry date
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     */
    update(params: {
        tokenId: string;
        expire?: string | null;
    }): Promise<Models.ResourceToken>;
    /**
     * Update a token by its unique ID. Use this endpoint to update a token's expiry date.
     *
     * @param {string} tokenId - Token unique ID.
     * @param {string | null} expire - File token expiry date
     * @throws {AppwriteException}
     * @returns {Promise<Models.ResourceToken>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    update(
        tokenId: string,
        expire?: string | null,
    ): Promise<Models.ResourceToken>;
    update(
        paramsOrFirst: { tokenId: string; expire?: string | null } | string,
        ...rest: [(string | null)?]
    ): Promise<Models.ResourceToken> {
        let params: { tokenId: string; expire?: string | null };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                tokenId: string;
                expire?: string | null;
            };
        } else {
            params = {
                tokenId: paramsOrFirst as string,
                expire: rest[0] as string | null,
            };
        }

        const tokenId = params.tokenId;
        const expire = params.expire;

        if (typeof tokenId === 'undefined' || tokenId === '') {
            throw new AppwriteException(
                'Missing required parameter: "tokenId"',
            );
        }
        const apiPath = '/tokens/{tokenId}'.replace(
            '{tokenId}',
            encodeURIComponent(String(tokenId)),
        );
        const apiPayload: Payload = {};
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'resourceToken',
            models: {
                resourceToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        resourceId: '',
                        resourceType: '',
                        expire: '',
                        secret: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a token by its unique ID.
     *
     * @param {string} params.tokenId - Token ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(params: { tokenId: string }): Promise<{}>;
    /**
     * Delete a token by its unique ID.
     *
     * @param {string} tokenId - Token ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    delete(tokenId: string): Promise<{}>;
    delete(paramsOrFirst: { tokenId: string } | string): Promise<{}> {
        let params: { tokenId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { tokenId: string };
        } else {
            params = {
                tokenId: paramsOrFirst as string,
            };
        }

        const tokenId = params.tokenId;

        if (typeof tokenId === 'undefined' || tokenId === '') {
            throw new AppwriteException(
                'Missing required parameter: "tokenId"',
            );
        }
        const apiPath = '/tokens/{tokenId}'.replace(
            '{tokenId}',
            encodeURIComponent(String(tokenId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }
}
